import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useDomainStore, useFamilyStore } from '@/hooks/useStores';
import { uiStore } from '@/stores/UIStore';
import { invitationsApi } from '@/api/invitationsWrapper';
import { FamilyMemberTypeahead, type FamilyMemberOption } from '@/components/family/FamilyMemberTypeahead';
import PlanUpgradeAlert from '@/components/shared/PlanUpgradeAlert';
import LanguageSelector from '@/components/language/LanguageSelector';
import { getUpgradeAwareErrorParts, isFamilyOwner } from '@/utils/familyPlan';
import type { AppLocale } from '@/i18n';
import './invite-family-member-form.css';

interface InviteFamilyMemberFormProps {
  familyId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const InviteFamilyMemberForm: React.FC<InviteFamilyMemberFormProps> = observer(
  ({ familyId, onSuccess, onCancel }) => {
    const { t } = useTranslation('invite');
    const { t: tCommon } = useTranslation('common');
    const domainStore = useDomainStore();
    const familyStore = useFamilyStore();
    const preferredLanguage = uiStore.preferredLanguage as AppLocale;
    const [email, setEmail] = useState('');
    const [familyMemberId, setFamilyMemberId] = useState<string>('');
    const [emailLocale, setEmailLocale] = useState<AppLocale>(preferredLanguage);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [upgradeHint, setUpgradeHint] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const isOwner = domainStore.selectedFamilyId === familyId && isFamilyOwner(domainStore.currentFamily?.userRole);

    React.useEffect(() => {
      setEmailLocale(preferredLanguage);
    }, [preferredLanguage]);

    // Auto-hide success message after 5 seconds
    React.useEffect(() => {
      if (success) {
        const timer = setTimeout(() => {
          setSuccess(false);
        }, 5000);
        return () => clearTimeout(timer);
      }
    }, [success]);

    // Get unclaimed family members (those without userId) and convert to FamilyMemberOption format
    const getUnclaimedFamilyMembers = (): FamilyMemberOption[] => {
      const peopleArray = Array.from(familyStore.people.values()).filter(
        (person) => !person.isPlaceholder && !person.userId
      );
      return peopleArray
        .sort((a, b) => {
          const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
          if (lastNameCompare !== 0) {
            return lastNameCompare;
          }
          return (a.firstName || '').localeCompare(b.firstName || '');
        })
        .map((person) => {
          // Get age if available
          const age = person.age;

          // Extract birth year from birthDate if available
          let birthYear: string | null = null;
          if (person.birthDate) {
            try {
              const birthDateObj = new Date(person.birthDate);
              if (!isNaN(birthDateObj.getTime())) {
                birthYear = birthDateObj.getFullYear().toString();
              }
            } catch {
              // Invalid date, leave birthYear as null
            }
          }

          // Get first spouse name if available
          let spouseName: string | null = null;
          if (person.spouseFamilyMemberIds.length > 0) {
            const firstSpouseId = person.selectedSpouseFamilyMemberId || person.spouseFamilyMemberIds[0];
            const spouse = familyStore.getPerson(firstSpouseId);
            if (spouse) {
              spouseName = spouse.fullName;
            }
          }

          return {
            familyMemberId: person.familyMemberId,
            fullName: person.fullName,
            firstName: person.firstName,
            lastName: person.lastName,
            age,
            birthYear,
            spouseName
          };
        });
    };

    const unclaimedMembers = getUnclaimedFamilyMembers();

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setUpgradeHint(null);
      setSuccess(false);

      if (!email.trim()) {
        setError(t('emailRequired'));
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError(t('emailInvalid'));
        return;
      }

      setIsSubmitting(true);
      try {
        const result = await invitationsApi.createInvitation({
          familyId,
          email: email.trim(),
          familyMemberId: familyMemberId || undefined,
          locale: emailLocale
        });

        if (result.success) {
          setSuccess(true);
          setEmail('');
          setFamilyMemberId('');
          if (onSuccess) {
            setTimeout(() => {
              onSuccess();
            }, 1500);
          }
        } else {
          const parts = getUpgradeAwareErrorParts(result.error, isOwner, t('failed'));
          setError(parts.message);
          setUpgradeHint(parts.upgradeHint);
        }
      } catch (err) {
        setError(t('failedRetry'));
        // eslint-disable-next-line no-console
        console.error('Invitation error:', err);
      } finally {
        setIsSubmitting(false);
      }
    };

    if (success) {
      return (
        <div className="invite-form-success">
          <p className="invite-form-success-text">{t('success')}</p>
          <button
            onClick={() => setSuccess(false)}
            className="invite-form-success-close"
            aria-label={t('dismissSuccess')}
          >
            ×
          </button>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="invite-form">
        {error && (
          <PlanUpgradeAlert
            message={error}
            upgradeHint={upgradeHint}
            onDismiss={() => {
              setError(null);
              setUpgradeHint(null);
            }}
            className="invite-form-error-alert"
          />
        )}

        <div className="invite-form-field">
          <label htmlFor="invite-email" className="invite-form-label">
            {t('emailLabel')}
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
            className="invite-form-input"
            placeholder={t('emailPlaceholder')}
          />
        </div>

        <div className="invite-form-field">
          <LanguageSelector id="invite-email-language" value={emailLocale} onValueChange={setEmailLocale} />
          <p className="invite-form-help-text">{t('emailLanguageHelp')}</p>
        </div>

        {unclaimedMembers.length > 0 && (
          <div className="invite-form-field">
            <FamilyMemberTypeahead
              id="invite-family-member"
              value={familyMemberId}
              onChange={setFamilyMemberId}
              members={unclaimedMembers}
              disabled={isSubmitting}
              placeholder={t('linkMemberPlaceholder')}
              label={t('linkMemberLabel')}
              helpText={t('linkMemberHelp')}
            />
          </div>
        )}

        <div className="invite-form-actions">
          {onCancel && (
            <button type="button" onClick={onCancel} disabled={isSubmitting} className="btn btn--ghost">
              {tCommon('cancel')}
            </button>
          )}
          <button type="submit" disabled={isSubmitting || !email.trim()} className="btn btn--primary">
            {isSubmitting ? t('sending') : t('send')}
          </button>
        </div>
      </form>
    );
  }
);

export default InviteFamilyMemberForm;
